import { useState, useContext, type FC } from 'react';
import {
  Button,
  Column,
  FontAwesomeIcon,
  Modal,
  Radio,
  Row,
  Text,
  useCurrentUser,
} from '@rivallapp/volosports-components';
import { faUserGroup } from '@fortawesome/pro-solid-svg-icons/faUserGroup';
import classNames from 'classnames/bind';
import groupBy from 'lodash-es/groupBy';

import styles from './styles.module.scss';
import { RegistrationContext } from '../../../../../contexts';
import { RegistrationActions } from '../../../../../contexts/RegistrationContext/RegistrationReducer';
import { useRegistrationData } from '../../../../../hooks';
import type { GetGameDetailsData } from '../../../../../hooks/useRegistrationData/QUERIES_AND_MUTATIONS';
import { DropInGenders } from '../../../../../shared/gender-enum';

const { UPDATE_REGISTRATION_STATE } = RegistrationActions;

const cx = classNames.bind(styles);

const TeamPreference: FC<{ game: GetGameDetailsData['game'] }> = props => {
  const { game } = props;
  const [chooseSlot, setChooseSlot] = useState(false);

  // @ts-expect-error RegistrationContext doesn't have types (yet)
  const [registerState, registerDispatch] = useContext(RegistrationContext);
  const {
    currentUser: { gender },
  } = useCurrentUser();

  const { selectedSlot } = registerState;

  const { registrationClosed, isEnrolledInProgram, isRsvpdForDropIn } = useRegistrationData();

  const { availableDropInSlots } = game;
  /**
   * We're grouping our drop in slots on two primary fields:
   * 1. team id
   * 2. existence of a note
   * If a team has multiple open slots but none have notes, only one group is created
   * However, if notes exist on any of those slots, one group will be created for each slot with a note, along with an additional group for any slots without notes (if there are any)
   *
   * For example:
   *   - Team 1: 2 any slots open, 1 slot with note "Goalie needed"
   *   - Team 2: 1 women-only slot
   * Output:
   *   - Team 1: 2 slot(s)
   *   - Team 1: 1 slot(s) - Note: Goalie needed
   *   - Team 2: 1 slot(s)
   */
  const groupedSlots = groupBy(
    availableDropInSlots,
    slot => `${slot.team._id}_${slot.note ? slot._id : false}`
  );
  const slotOptions = Object.entries(groupedSlots).map(([groupKey, slots]) => {
    const groupHasNote = groupKey.split('_')[1] !== 'false';
    const groupGender = slots[0]!.gender!;
    return (
      <Radio
        key={groupKey}
        _stack={{
          alignItems: 'flex-start',
        }}
        value={groupKey}
        marginBottom={6}
        // @ts-expect-error TS doesn't like that gender is a string being used to access an object like this, since the keys come from an enum
        isDisabled={!DropInGenders[groupGender].validGenders.includes(gender)}
      >
        <Column alignItems="flex-start">
          <Row>
            <Text>{slots[0]?.team.name}</Text>
            <Text type="caption" fontSize="md" marginLeft={4}>
              {slots.length} slot(s) available
            </Text>
          </Row>
          {groupHasNote && (
            <Text marginLeft={8} type="caption">
              Note: {slots[0]?.note}
            </Text>
          )}
        </Column>
      </Radio>
    );
  });

  return (
    <div className={cx('team-preference')}>
      <Modal isOpen={chooseSlot} onClose={() => setChooseSlot(false)} useRNModal size="xl">
        <Modal.Content>
          <Modal.Header>Choose a specific spot</Modal.Header>
          <Modal.CloseButton />
          <Modal.Body>
            <Radio.Group
              name="drop_in_slot_selector"
              accessibilityLabel="drop in slot selector"
              value={selectedSlot?.key}
              onChange={slotKey =>
                registerDispatch({
                  type: UPDATE_REGISTRATION_STATE,
                  update: { selectedSlot: { key: slotKey, value: groupedSlots[slotKey] } },
                })
              }
            >
              {slotOptions}
            </Radio.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              onPress={() => {
                registerDispatch({
                  type: UPDATE_REGISTRATION_STATE,
                  update: { selectedSlot: null },
                });
                setChooseSlot(false);
              }}
              marginRight={2}
              variant="outline"
            >
              No slot preference
            </Button>
            <Button onPress={() => setChooseSlot(false)}>Confirm choice</Button>
          </Modal.Footer>
        </Modal.Content>
      </Modal>

      <div className="container">
        <div className="row align-items-center col-12 p-2 mt-2 mb-2">
          <FontAwesomeIcon colorScheme="primary" icon={faUserGroup} size="sm" />
          <div className="pl-2">Choose Spot:</div>
          <div className="pl-1">{selectedSlot?.value[0].team.name ?? 'No preference'}</div>
          <Button
            variant="link"
            size="xs"
            paddingX={2}
            paddingTop={1}
            marginLeft={1}
            onPress={() => setChooseSlot(true)}
            disabled={
              (isEnrolledInProgram && !game) || (isRsvpdForDropIn && game) || registrationClosed
            }
            _text={{ fontSize: 'sm' }}
          >
            select spot
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TeamPreference;
